import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  Paper,
  useTheme,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Card,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Mail, LocationOn } from "@mui/icons-material";
import Header from "../common/Header";
import Footer from "../common/Footer";
import axios from "axios";

// Obtener la URL del backend del entorno
const API_URL = process.env.REACT_APP_API_URL;

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | undefined;
    message: string;
  }>({ type: undefined, message: "" });
  const [configError, setConfigError] = useState<string | null>(null);
  
  // Verificar la configuración al cargar el componente
  useEffect(() => {
    if (!API_URL) {
      setConfigError("API URL not configured. Please set REACT_APP_API_URL in your environment variables.");
      console.error("Missing REACT_APP_API_URL environment variable");
    }
  }, []);

  const validateForm = () => {
    const newErrors: Partial<ContactFormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Verificar si la API URL está configurada
    if (!API_URL) {
      setSubmitStatus({
        type: "error",
        message: "API URL not configured. Contact form submission is disabled.",
      });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus({ type: undefined, message: "" });
    
    try {
      console.log('Sending form to:', `${API_URL}/contact`);
      
      // Enviar datos al backend
      await axios.post(`${API_URL}/contact`, {
        ...formData,
        type: 'contact'
      });
      
      // Reset form after successful submission
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      
      setSubmitStatus({
        type: "success",
        message: "Your message has been sent successfully. We'll get back to you soon!",
      });
    } catch (error) {
      console.error('Error sending contact form:', error);
      setSubmitStatus({
        type: "error",
        message: "There was an error sending your message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Contact Us | Tranzcribe</title>
        <meta
          name="description"
          content="Get in touch with our team for support, feedback, or inquiries. We're here to help you make the most of your transcription experience."
        />
      </Helmet>

      {/* Navigation Bar - Sticky */}
      <Header />

      {/* Contact Content */}
      <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom align="center">
          Get in Touch
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 700, mx: "auto" }}>
          Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </Typography>

        {configError && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {configError}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Contact Info */}
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Contact Information
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
                  Reach out to us through any of these channels and we'll be happy to help.
                </Typography>

                <Box sx={{ mt: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Mail sx={{ color: theme.palette.primary.main, mr: 2 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">Email</Typography>
                      <Typography variant="body2" color="text.secondary">
                        hey@tranzcribe.com
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <LocationOn sx={{ color: theme.palette.primary.main, mr: 2 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">Office</Typography>
                      <Typography variant="body2" color="text.secondary">
                        447 Broadway 2nd Floor #737
                        <br />
                        New York, NY 10013
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Send a Message
              </Typography>

              {submitStatus.type && (
                <Alert 
                  severity={submitStatus.type} 
                  sx={{ mb: 3 }}
                  onClose={() => setSubmitStatus({ type: undefined, message: "" })}
                >
                  {submitStatus.message}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Your Name"
                      name="name"
                      fullWidth
                      variant="outlined"
                      value={formData.name}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      disabled={isSubmitting || !!configError}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Address"
                      name="email"
                      type="email"
                      fullWidth
                      variant="outlined"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      disabled={isSubmitting || !!configError}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Subject"
                      name="subject"
                      fullWidth
                      variant="outlined"
                      value={formData.subject}
                      onChange={handleChange}
                      error={!!errors.subject}
                      helperText={errors.subject}
                      disabled={isSubmitting || !!configError}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Message"
                      name="message"
                      multiline
                      rows={6}
                      fullWidth
                      variant="outlined"
                      value={formData.message}
                      onChange={handleChange}
                      error={!!errors.message}
                      helperText={errors.message}
                      disabled={isSubmitting || !!configError}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={isSubmitting || !!configError}
                      sx={{
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "600",
                        textTransform: "none",
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <CircularProgress size={24} sx={{ mr: 1, color: "white" }} />
                          Sending...
                        </>
                      ) : (
                        "Send Message"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Contact; 